import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import TimeAgo from 'react-timeago';
import { useEvalSetStatus } from '../hooks/useEvalSetStatus';
import { useApiFetch } from '../hooks/useApiFetch';
import type {
  EvalSetStatus,
  EventBrief,
  JobStatus,
  MetricsQueryResult,
  SamplePhase,
  SampleStatus,
  StatusProgress,
  TaskStatus,
  WaitingSample,
} from '../types/monitoring';
import { isTerminal } from '../types/monitoring';
import {
  dotClassForStatus,
  friendlyTroubleReason,
  labelForStatus,
} from '../utils/jobStatus';
import '../index.css';
import { getEvalSetDisplayName } from '../utils/evalSetDisplayName';

export default function EvalSetStatusPage() {
  const { evalSetId } = useParams<{ evalSetId: string }>();
  const result = useEvalSetStatus({ evalSetId: evalSetId ?? null });

  // The app shell pins html/body/#root to height:100% with overflow:hidden, so
  // this page must own its own vertical scroll — otherwise a sample list taller
  // than the viewport gets clipped with no way to reach the bottom.
  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-6xl px-4 py-6">
        <div className="mb-4 flex items-center gap-3">
          <Link
            to="/jobs"
            className="text-sm text-brand-700 hover:text-brand-600 dark:text-brand-300 dark:hover:text-brand-200"
          >
            ← Jobs
          </Link>
          <h1
            title={evalSetId}
            className="truncate text-sm font-medium text-gray-900 dark:text-gray-100"
          >
            {getEvalSetDisplayName(evalSetId ?? '')}
          </h1>
        </div>

        {result.state === 'loading' && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Loading status…
          </p>
        )}
        {result.state === 'error' && (
          <p className="text-sm text-red-600 dark:text-red-400">
            {result.message}
          </p>
        )}
        {result.state === 'ready' && <StatusBody data={result.data} />}
      </div>
    </div>
  );
}

function StatusBody({ data }: { data: EvalSetStatus }) {
  const waiting = data.waiting_samples;
  const errorSources = Object.keys(data.errors);
  return (
    <div className="space-y-4">
      <StatusHeader data={data} />
      {errorSources.length > 0 && (
        <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
          Some data could not be loaded ({errorSources.join(', ')}); showing
          what we have. Status may be incomplete.
        </p>
      )}
      {waiting.length > 0 && <WaitingSamplesCard samples={waiting} />}
      {data.tasks.length > 0
        ? data.tasks.map(task => <TaskCard key={task.log_file} task={task} />)
        : waiting.length === 0 && <EmptyTasks data={data} />}
    </div>
  );
}

function WaitingSamplesCard({ samples }: { samples: WaitingSample[] }) {
  return (
    <section className="rounded-lg border border-amber-200 bg-amber-50/40 dark:border-amber-900 dark:bg-amber-950/20">
      <div className="border-b border-amber-200 px-4 py-3 dark:border-amber-900">
        <span className="font-medium text-gray-900 dark:text-gray-100">
          Waiting for Kubernetes
        </span>
        <span className="ml-1 text-gray-500 dark:text-gray-400">
          ({samples.length})
        </span>
        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
          sandbox pods not yet scheduled — these samples have not started
        </span>
      </div>
      <table className="w-full text-left text-xs">
        <thead className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <tr>
            <th className="px-4 py-2 font-medium">Sample</th>
            <th className="px-2 py-2 font-medium">Pod</th>
            <th className="px-2 py-2 font-medium">Reason</th>
          </tr>
        </thead>
        <tbody>
          {samples.map(s => (
            <tr
              key={s.pod_name}
              className="border-t border-amber-100 align-top dark:border-amber-900/60"
            >
              <td className="px-4 py-2 font-mono">{s.sample_id ?? '—'}</td>
              <td className="px-2 py-2 font-mono text-gray-500 dark:text-gray-400">
                {s.pod_name}
              </td>
              <td className="px-2 py-2">
                <div className="space-y-1">
                  <span
                    className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${
                      s.reason
                        ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                    }`}
                  >
                    {s.reason
                      ? (friendlyTroubleReason(s.reason) ?? s.reason)
                      : 'scheduling'}
                  </span>
                  {s.detail && (
                    <div className="max-w-xl text-[11px] text-gray-500 dark:text-gray-400">
                      {s.detail}
                    </div>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function EmptyTasks({ data }: { data: EvalSetStatus }) {
  const pods = data.pods?.pods ?? [];
  const hasRunner = pods.some(p => p.component === 'runner');
  const sandboxPending = pods.filter(
    p => p.component === 'sandbox' && p.phase === 'Pending'
  ).length;

  let message: string;
  if (data.status === 'complete' || data.status === 'failed') {
    message = 'No tasks were recorded for this eval set.';
  } else if (sandboxPending > 0) {
    message = `Waiting for Kubernetes — ${sandboxPending} sandbox pod${
      sandboxPending === 1 ? '' : 's'
    } pending. Samples appear once they start.`;
  } else if (hasRunner) {
    message =
      'Runner starting up (installing task packages). Tasks appear once the first one begins.';
  } else {
    message = 'Waiting for the runner pod to start…';
  }
  return <p className="text-sm text-gray-500 dark:text-gray-400">{message}</p>;
}

function StatusHeader({ data }: { data: EvalSetStatus }) {
  const trouble = friendlyTroubleReason(data.trouble_reason);
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
      <div className="flex flex-wrap items-center gap-3">
        <span
          aria-hidden="true"
          className={`inline-block h-2.5 w-2.5 rounded-full ${dotClassForStatus(
            data.status
          )}`}
        />
        <span className="font-medium text-gray-900 dark:text-gray-100">
          {labelForStatus(data.status)}
        </span>
        {trouble && (
          <span className="text-sm text-amber-700 dark:text-amber-400">
            — {trouble}
          </span>
        )}
        {data.created_by && (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Created by {data.created_by}
          </span>
        )}
        <div className="ml-auto flex items-center gap-3">
          <RunnerHealth metrics={data.metrics} />
          {data.is_owner_or_admin && (
            <OwnerActions jobId={data.job_id} status={data.status} />
          )}
        </div>
      </div>
      <div className="mt-3">
        <ProgressBar progress={data.progress} />
        <ProgressLegend progress={data.progress} />
      </div>
    </section>
  );
}

function OwnerActions({ jobId, status }: { jobId: string; status: JobStatus }) {
  const { apiFetch } = useApiFetch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<'stop' | 'delete' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['eval-set-status', jobId] });

  const onStop = async () => {
    if (
      !window.confirm(
        'Stop this eval set? Active samples are scored with partial work and the run exits.'
      )
    )
      return;
    setBusy('stop');
    setError(null);
    const res = await apiFetch(
      `/eval_sets/${encodeURIComponent(jobId)}/stop`,
      { method: 'POST' },
      { returnNonOkResponse: true }
    );
    setBusy(null);
    if (!res || !res.ok) {
      setError(
        res?.status === 403
          ? 'You do not have permission to stop this eval set.'
          : `Stop failed${res ? ` (${res.status})` : ''}.`
      );
      return;
    }
    void refresh();
  };

  const onDelete = async () => {
    if (
      !window.confirm(
        'Force stop this eval set? This kills the runner and tears down its Kubernetes resources immediately. Logs and results are kept.'
      )
    )
      return;
    setBusy('delete');
    setError(null);
    const res = await apiFetch(
      `/eval_sets/${encodeURIComponent(jobId)}`,
      { method: 'DELETE' },
      { returnNonOkResponse: true }
    );
    setBusy(null);
    if (res && res.ok) {
      navigate('/jobs');
      return;
    }
    setError(
      res?.status === 403
        ? 'You do not have permission to force stop this eval set.'
        : `Force stop failed${res ? ` (${res.status})` : ''}.`
    );
    void refresh();
  };

  return (
    <div className="flex items-center gap-2">
      {error && (
        <span className="text-xs text-red-600 dark:text-red-400">{error}</span>
      )}
      {!isTerminal(status) && (
        <button
          type="button"
          onClick={onStop}
          disabled={busy !== null}
          className="rounded border border-amber-300 bg-white px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-50 disabled:opacity-50 dark:border-amber-700 dark:bg-gray-900 dark:text-amber-300 dark:hover:bg-amber-950/40"
        >
          {busy === 'stop' ? 'Stopping…' : 'Stop'}
        </button>
      )}
      <button
        type="button"
        onClick={onDelete}
        disabled={busy !== null}
        className="rounded border border-red-300 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:bg-gray-900 dark:text-red-400 dark:hover:bg-red-950/40"
      >
        {busy === 'delete' ? 'Stopping…' : 'Force stop'}
      </button>
    </div>
  );
}

function RunnerHealth({
  metrics,
}: {
  metrics: Record<string, MetricsQueryResult> | null;
}) {
  if (!metrics) return null;
  const cpu = metrics['runner_cpu']?.value ?? null;
  const mem = metrics['runner_memory']?.value ?? null;
  const sandboxPods = metrics['sandbox_pods']?.value ?? null;
  const sandboxGpus = metrics['sandbox_gpus']?.value ?? null;
  const parts: string[] = [];
  if (cpu != null) parts.push(`CPU ${formatCpu(cpu)}`);
  if (mem != null) parts.push(`mem ${formatBytes(mem)}`);
  if (sandboxPods != null) parts.push(`${sandboxPods} sandbox pods`);
  if (sandboxGpus != null && sandboxGpus > 0) parts.push(`${sandboxGpus} GPUs`);
  if (parts.length === 0) return null;
  return (
    <span className="text-xs text-gray-500 dark:text-gray-400">
      runner: {parts.join(' · ')}
    </span>
  );
}

function ProgressBar({ progress }: { progress: StatusProgress }) {
  const total = progress.total || 1;
  const segments: { value: number; className: string }[] = [
    { value: progress.completed, className: 'bg-brand-500' },
    { value: progress.running, className: 'bg-blue-500' },
    { value: progress.errored, className: 'bg-red-500' },
    { value: progress.limit, className: 'bg-amber-500' },
    { value: progress.canceled, className: 'bg-gray-400 dark:bg-gray-500' },
    { value: progress.waiting, className: 'bg-gray-300 dark:bg-gray-600' },
  ];
  return (
    <div
      className="flex h-2.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800"
      role="img"
      aria-label={`${progress.completed} of ${progress.total} complete`}
    >
      {segments.map((seg, i) =>
        seg.value > 0 ? (
          <div
            key={i}
            className={seg.className}
            style={{ width: `${(seg.value / total) * 100}%` }}
          />
        ) : null
      )}
    </div>
  );
}

function ProgressLegend({ progress }: { progress: StatusProgress }) {
  return (
    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-600 dark:text-gray-400">
      <LegendItem
        dot="bg-brand-500"
        label="completed"
        value={progress.completed}
      />
      <LegendItem dot="bg-blue-500" label="running" value={progress.running} />
      <LegendItem dot="bg-red-500" label="errored" value={progress.errored} />
      <LegendItem dot="bg-amber-500" label="limit" value={progress.limit} />
      {progress.canceled > 0 && (
        <LegendItem
          dot="bg-gray-400 dark:bg-gray-500"
          label="canceled"
          value={progress.canceled}
        />
      )}
      <LegendItem
        dot="bg-gray-300 dark:bg-gray-600"
        label="waiting"
        value={progress.waiting}
      />
      <span className="font-medium text-gray-500 dark:text-gray-400">
        {progress.total} total
      </span>
    </div>
  );
}

function LegendItem({
  dot,
  label,
  value,
}: {
  dot: string;
  label: string;
  value: number;
}) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`inline-block h-2 w-2 rounded-full ${dot}`} />
      {label} {value}
    </span>
  );
}

function TaskCard({ task }: { task: TaskStatus }) {
  const [expanded, setExpanded] = useState(false);
  const trouble = friendlyTroubleReason(task.trouble_reason);
  return (
    <section className="rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
        aria-expanded={expanded}
      >
        <span className="font-medium text-gray-900 dark:text-gray-100">
          {task.task_name}
        </span>
        {task.live && (
          <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
            live
          </span>
        )}
        {trouble && (
          <span className="rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-950/40 dark:text-red-300">
            {trouble}
          </span>
        )}
        <span className="ml-auto w-48 shrink-0">
          <ProgressBar progress={task.progress} />
        </span>
        <span className="w-20 shrink-0 text-right text-xs text-gray-500 dark:text-gray-400">
          {task.progress.completed}/{task.progress.total}
        </span>
      </button>
      {expanded && <SampleTable samples={task.samples} />}
    </section>
  );
}

function SampleTable({ samples }: { samples: SampleStatus[] }) {
  if (samples.length === 0) {
    return (
      <p className="border-t border-gray-100 px-4 py-3 text-xs text-gray-500 dark:border-gray-800 dark:text-gray-400">
        No samples tracked yet.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto border-t border-gray-100 dark:border-gray-800">
      <table className="w-full text-left text-xs">
        <thead className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <tr>
            <th className="px-4 py-2 font-medium">Sample</th>
            <th className="px-2 py-2 font-medium">Phase</th>
            <th className="px-2 py-2 font-medium">Retries</th>
            <th className="px-2 py-2 font-medium">Tokens</th>
            <th className="px-2 py-2 font-medium">Time</th>
            <th className="px-2 py-2 font-medium">Score</th>
            <th className="px-2 py-2 font-medium">Last event</th>
          </tr>
        </thead>
        <tbody>
          {samples.map(sample => (
            <SampleRow key={`${sample.id}-${sample.epoch}`} sample={sample} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SampleRow({ sample }: { sample: SampleStatus }) {
  const lastEvent = sample.last_events.at(-1) ?? null;
  const label = `${sample.id}${sample.epoch > 1 ? ` · e${sample.epoch}` : ''}`;
  return (
    <tr className="border-t border-gray-50 align-top dark:border-gray-800">
      <td className="px-4 py-2 font-mono">
        {sample.uuid ? (
          <Link
            to={`/permalink/sample/${encodeURIComponent(sample.uuid)}`}
            className="text-brand-700 hover:text-brand-600 dark:text-brand-300 dark:hover:text-brand-200"
          >
            {label}
          </Link>
        ) : (
          label
        )}
      </td>
      <td className="px-2 py-2">
        <PhaseChip phase={sample.phase} limit={sample.limit} />
      </td>
      <td className="px-2 py-2">
        {sample.retries == null ? (
          <span className="text-gray-400 dark:text-gray-600">—</span>
        ) : sample.retries > 0 ? (
          <span className="text-amber-700 dark:text-amber-400">
            {sample.retries}
          </span>
        ) : (
          <span className="text-gray-400 dark:text-gray-600">0</span>
        )}
      </td>
      <td className="px-2 py-2 text-gray-600 dark:text-gray-400">
        {sample.total_tokens != null
          ? sample.total_tokens.toLocaleString()
          : '—'}
      </td>
      <td className="px-2 py-2 text-gray-600 dark:text-gray-400">
        {sample.total_time != null
          ? formatDuration(sample.total_time)
          : sample.working_time != null
            ? formatDuration(sample.working_time)
            : '—'}
      </td>
      <td className="px-2 py-2 text-gray-600 dark:text-gray-400">
        {sample.latest_score ?? '—'}
      </td>
      <td className="px-2 py-2">
        <LastEvent event={lastEvent} />
      </td>
    </tr>
  );
}

function LastEvent({ event }: { event: EventBrief | null }) {
  if (!event)
    return <span className="text-gray-400 dark:text-gray-600">—</span>;
  return (
    <div className="space-y-0.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-700 dark:bg-gray-800 dark:text-gray-300">
          {event.type}
        </span>
        {event.description && (
          <span className="text-gray-700 dark:text-gray-300">
            {event.description}
          </span>
        )}
        {event.pending && (
          <span className="text-[10px] text-blue-600 dark:text-blue-400">
            in progress
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-x-2 text-[11px] text-gray-500 dark:text-gray-400">
        {event.timestamp && (
          <span title={event.timestamp}>
            <TimeAgo date={event.timestamp} />
          </span>
        )}
        {event.duration_seconds != null && (
          <span>took {formatDuration(event.duration_seconds)}</span>
        )}
      </div>
    </div>
  );
}

const PHASE_CLASSES: Record<SamplePhase, string> = {
  waiting: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  init: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300',
  running: 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
  scoring:
    'bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300',
  completed:
    'bg-brand-100 text-brand-700 dark:bg-brand-700/30 dark:text-brand-200',
  errored: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  limit: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
  canceled: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
};

function PhaseChip({
  phase,
  limit,
}: {
  phase: SamplePhase;
  limit: string | null;
}) {
  const text = phase === 'limit' && limit ? `limit: ${limit}` : phase;
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${PHASE_CLASSES[phase]}`}
    >
      {text}
    </span>
  );
}

function formatCpu(nanocores: number): string {
  const cores = nanocores / 1_000_000_000;
  return `${cores.toFixed(cores < 1 ? 2 : 1)} cores`;
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GiB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(0)} MiB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KiB`;
  return `${bytes} B`;
}

function formatDuration(seconds: number): string {
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  // Round once to whole seconds, then split — computing minutes and seconds
  // independently can yield "1m 60s" when the seconds part rounds up.
  const total = Math.round(seconds);
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}m ${secs}s`;
}
